// IndexedDB-backed store for large background media (video/gif/image blobs).
// Keeps localStorage lean — bg state only holds a reference like
// `indexeddb:<key>`, actual Blob lives here. Optionally mirrors blobs to the
// backend so scenes survive across devices/browsers.

import { API_URL, UPLOAD_URL } from '../../api/api';

// Every /api/settings/backgrounds endpoint is behind authenticate(), so each
// request from this module has to carry the bearer token. Without it uploads
// and lookups came back 401 and were swallowed as "sync failed".
function authHeaders(extra) {
  const headers = { ...(extra || {}) };
  try {
    const token = localStorage.getItem('token');
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (e) {
    /* storage blocked — the request will just be unauthenticated */
  }
  return headers;
}

const DB_NAME = 'emhub_bg_media';
const STORE = 'blobs';
const VERSION = 1;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      // A connection can be closed out from under us — the browser does it on
      // navigation/reload, and another tab upgrading the DB does it too. This
      // promise caches the handle for the life of the module, so without
      // these we keep handing out a dead connection and every later
      // db.transaction() throws "The database connection is closing".
      db.onclose = () => { dbPromise = null; };
      db.onversionchange = () => { db.close(); dbPromise = null; };
      resolve(db);
    };
    req.onerror = () => { dbPromise = null; reject(req.error); };
  });
  return dbPromise;
}

/**
 * Run a transaction, reopening once if the cached connection turned out to be
 * closing. `db.transaction()` throws InvalidStateError *synchronously* in that
 * case, and the close often isn't observable until we try to use it.
 */
async function withStore(mode, run) {
  const attempt = async () => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      tx.onerror = () => reject(tx.error);
      run(tx.objectStore(STORE), resolve, reject, tx);
    });
  };
  try {
    return await attempt();
  } catch (e) {
    if (e?.name !== 'InvalidStateError') throw e;
    dbPromise = null; // drop the dead handle and try once with a fresh one
    return attempt();
  }
}

export async function putBlob(key, blob) {
  return withStore('readwrite', (store, resolve, reject, tx) => {
    store.put(blob, key);
    tx.oncomplete = () => resolve(key);
  });
}

export async function getBlob(key) {
  return withStore('readonly', (store, resolve) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result || null);
  });
}

export async function deleteBlob(key) {
  return withStore('readwrite', (store, resolve, reject, tx) => {
    store.delete(key);
    tx.oncomplete = () => resolve();
  });
}

export async function getAllKeys() {
  return withStore('readonly', (store, resolve) => {
    const req = store.getAllKeys();
    req.onsuccess = () => resolve(req.result || []);
  });
}

// In-memory cache: key -> object URL (blob: URL or remote http URL).
const urlCache = new Map();

// Persisted map of key -> remote URL so a new device/browser can render a
// scene even before we've re-hydrated the blob into IDB.
const REMOTE_MAP_KEY = 'emhub_bg_remote_urls_v1';

function loadRemoteMap() {
  try {
    const raw = localStorage.getItem(REMOTE_MAP_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function saveRemoteMap(map) {
  try {
    localStorage.setItem(REMOTE_MAP_KEY, JSON.stringify(map));
  } catch (e) {
    console.warn('[backgrounds] remote map persist failed', e);
  }
}
export function getRemoteUrl(key) {
  return loadRemoteMap()[key] || null;
}
export function setRemoteUrl(key, url) {
  const map = loadRemoteMap();
  map[key] = url;
  saveRemoteMap(map);
}
export function forgetRemoteUrl(key) {
  const map = loadRemoteMap();
  if (key in map) {
    delete map[key];
    saveRemoteMap(map);
  }
}

export async function resolveToUrl(key) {
  if (urlCache.has(key)) return urlCache.get(key);
  const blob = await getBlob(key);
  if (blob) {
    const url = URL.createObjectURL(blob);
    urlCache.set(key, url);
    return url;
  }
  // IDB miss — try the backend fallback. Use the remote URL directly so the
  // user sees it right away, and re-hydrate IDB in the background.
  const remote = mediaSrcToUrl(getRemoteUrl(key) || (await fetchRemoteUrl(key)));
  if (remote) {
    urlCache.set(key, remote);
    hydrateFromRemote(key, remote).catch((e) =>
      console.warn('[backgrounds] rehydrate failed', key, e)
    );
    return remote;
  }
  return null;
}

async function fetchRemoteUrl(key) {
  if (!API_URL) return null;
  try {
    const res = await fetch(
      `${API_URL}/api/settings/backgrounds/media/${encodeURIComponent(key)}`,
      { headers: authHeaders() }
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.url) {
      setRemoteUrl(key, data.url);
      return data.url;
    }
  } catch (e) {
    console.warn('[backgrounds] lookup failed', key, e);
  }
  return null;
}

async function hydrateFromRemote(key, url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return;
    const blob = await res.blob();
    await putBlob(key, blob);
  } catch (e) {
    // Silent — next resolve will try again.
  }
}

export function getCachedUrl(key) {
  return urlCache.get(key) || null;
}

export function releaseUrl(key) {
  const url = urlCache.get(key);
  if (url) {
    URL.revokeObjectURL(url);
    urlCache.delete(key);
  }
}

export async function preloadAll(keys) {
  const out = {};
  await Promise.all(
    keys.map(async (k) => {
      // One unreadable key must not fail the whole preload.
      try {
        const url = await resolveToUrl(k);
        if (url) out[k] = url;
      } catch (e) {
        console.warn('[backgrounds] preload skipped', k, e?.message);
      }
    })
  );
  return out;
}

// Helpers for the "indexeddb:<key>" src convention used in bg state.
export const IDB_PREFIX = 'indexeddb:';

export function isIdbSrc(src) {
  return typeof src === 'string' && src.startsWith(IDB_PREFIX);
}

export function idbKey(src) {
  return isIdbSrc(src) ? src.slice(IDB_PREFIX.length) : null;
}

export function makeIdbSrc(key) {
  return IDB_PREFIX + key;
}

// ---------- public URL resolution ----------
// Uploaded media is stored as a root-relative path ("/uploads/backgrounds/x.jpg")
// and has to be joined to UPLOAD_URL, not API_URL: in production the API answers
// at the site root while the static uploads sit behind the `/backend` prefix, so
// only UPLOAD_URL carries the piece the server can't know about. Same helper
// shape as UserAvatar/wsApi/useImageCache use for every other upload.
const UPLOADS_MARKER = '/uploads/';

export function mediaSrcToUrl(src) {
  if (!src || typeof src !== 'string') return src;
  const s = src.trim();
  // Gradients, colours, inline data and blob handles are already renderable.
  if (
    /^(linear|radial|conic)-gradient/i.test(s) ||
    s.startsWith('#') ||
    s.startsWith('rgb') ||
    s.startsWith('hsl') ||
    s.startsWith('data:') ||
    s.startsWith('blob:') ||
    isIdbSrc(s)
  ) {
    return s;
  }
  if (s.startsWith(UPLOADS_MARKER)) return `${UPLOAD_URL}${s}`;
  if (/^https?:\/\//i.test(s)) {
    // Rows written before the URLs went relative are absolute AND wrong — they
    // point at the bare host with no `/backend` prefix (and over http, which an
    // https page blocks as mixed content). Rebuild those from the path so old
    // scenes render without needing a database migration. Anything else that's
    // absolute — Unsplash seeds, say — is left alone.
    if (s.startsWith(UPLOAD_URL)) return s;
    const i = s.indexOf(UPLOADS_MARKER);
    if (i !== -1) return `${UPLOAD_URL}${s.slice(i)}`;
    return s;
  }
  return s;
}

// ---------- upload helper used by SceneBuilder / BackgroundsSettings ----------
// Enforces a 10 MB file cap and re-encodes images so the upload fits under the
// reverse proxy's request-body limit before it ever leaves the browser.

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

// The API sits behind a reverse proxy whose body limit is far stricter than
// multer's 25 MB: nginx defaults `client_max_body_size` to 1 MB, and that limit
// is what answered "413 Request Entity Too Large" on the live host while the
// identical upload succeeded against a bare dev server. Backgrounds are purely
// decorative, so instead of depending on proxy config we shrink every image to
// something that comfortably clears that default.
export const UPLOAD_TARGET_BYTES = 900 * 1024;
// 2560px on the long edge covers any realistic display without carrying a
// 12-megapixel phone photo's worth of pixels.
export const UPLOAD_MAX_DIM = 2560;

// canvas.toBlob() silently falls back to PNG for a format the browser can't
// encode, which for a transparent layer would balloon the payload instead of
// shrinking it. Probe once so we know whether WebP is on the table.
let webpOk = null;
function supportsWebp() {
  if (webpOk !== null) return webpOk;
  try {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    webpOk = c.toDataURL('image/webp').startsWith('data:image/webp');
  } catch (e) {
    webpOk = false;
  }
  return webpOk;
}

// Draw the bitmap scaled so its long edge is `dim` and encode it.
async function encodeAt(bmp, dim, type, quality) {
  const longest = Math.max(bmp.width, bmp.height);
  const scale = Math.min(1, dim / longest);
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bmp, 0, 0, w, h);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

/**
 * Shrink an image until it fits `targetBytes`, trading resolution then quality.
 * Returns a Blob ready to store and upload — or the original file when it's
 * already small enough, isn't an image, or can't survive a canvas round-trip
 * (animated GIFs, SVGs, video).
 */
export async function compressForUpload(file, opts = {}) {
  const maxDim = opts.maxDim || UPLOAD_MAX_DIM;
  const targetBytes = opts.targetBytes || UPLOAD_TARGET_BYTES;
  if (!file) throw new Error('No file provided');
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). ` +
        `Max ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`
    );
  }
  const type = (file.type || '').toLowerCase();
  // An animated GIF loses its animation through a canvas, and an SVG has no
  // pixels to resample — leave both (and anything non-image) exactly as-is.
  if (
    !type.startsWith('image/') ||
    type === 'image/gif' ||
    type === 'image/svg+xml' ||
    typeof createImageBitmap !== 'function'
  ) {
    return file;
  }
  // PNG/WebP sources may carry transparency that scene layers depend on, so
  // they must never be flattened into JPEG. WebP keeps alpha *and* takes a
  // quality argument; without it we can only resize the PNG.
  const hasAlpha = type === 'image/png' || type === 'image/webp';
  const encodeType = hasAlpha
    ? supportsWebp()
      ? 'image/webp'
      : 'image/png'
    : 'image/jpeg';
  const lossless = encodeType === 'image/png';

  let bmp;
  try {
    bmp = await createImageBitmap(file);
  } catch (e) {
    // Undecodable here doesn't mean unusable — putBlob can still store it.
    return file;
  }
  const longest = Math.max(bmp.width, bmp.height);
  if (longest <= maxDim && file.size <= targetBytes) {
    bmp.close?.();
    return file;
  }

  let dim = Math.min(longest, maxDim);
  let best = null;
  try {
    // Resize, then trade quality, then resize again. A handful of rounds is
    // plenty to bring any phone photo under the budget, and we stop the moment
    // one fits rather than compressing further than needed.
    for (let round = 0; round < 4; round += 1) {
      const qualities = lossless ? [undefined] : [0.85, 0.7, 0.55];
      for (const q of qualities) {
        // eslint-disable-next-line no-await-in-loop
        const blob = await encodeAt(bmp, dim, encodeType, q);
        if (!blob) continue;
        if (!best || blob.size < best.size) best = blob;
        if (blob.size <= targetBytes) return blob;
      }
      dim = Math.round(dim * 0.75);
      if (dim < 640) break;
    }
  } finally {
    bmp.close?.();
  }
  // Never made the budget (a huge lossless PNG, say) — send the smallest
  // version we produced, which is still far below the original.
  return best && best.size < file.size ? best : file;
}

// Back-compat alias: same cap + resize behaviour, now byte-aware too.
export async function maybeDownscale(file, maxDim = UPLOAD_MAX_DIM) {
  return compressForUpload(file, { maxDim });
}

/**
 * Upload a File/Blob into IndexedDB (and best-effort sync to backend once the
 * API exists) and return `{ key, src, blob }`. `src` is the `indexeddb:<key>`
 * reference you can drop straight into a layer's `src` field.
 */
export async function uploadLayerFile(file) {
  const processed = await maybeDownscale(file);
  const key = `bg-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  await putBlob(key, processed);
  // Fire-and-forget backend sync. Currently a no-op seam — see syncToBackend().
  syncToBackend(key, processed).catch((err) =>
    console.warn('[backgrounds] deferred backend sync failed:', err)
  );
  return { key, src: makeIdbSrc(key), blob: processed };
}

/**
 * Mirror a media blob to the backend so it survives across devices/browsers.
 * Idempotent on `key` — the server replaces the file at `<key>.<ext>` on
 * re-upload. On success, caches `key -> remoteUrl` in a persisted map that
 * `resolveToUrl` can fall back to when the IDB cache is cold.
 */
export async function syncToBackend(key, blob) {
  if (!API_URL || !key || !blob) return null;
  const ext = mimeToExt(blob.type);
  const form = new FormData();
  form.append('key', key);
  form.append('file', blob, `${key}${ext}`);
  const res = await fetch(`${API_URL}/api/settings/backgrounds/media`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  // A 413 never reaches our code — the proxy (or multer) rejects the body
  // first — so translate it into something the user can act on instead of the
  // bare "HTTP 413" that used to end up in the console and nowhere else.
  if (res.status === 413) {
    throw new Error(
      `The server rejected this file as too large (${(blob.size / 1024 / 1024).toFixed(1)} MB). ` +
        'Try a smaller image, or raise the upload limit on the server.'
    );
  }
  if (!res.ok) throw new Error(`Upload failed (HTTP ${res.status})`);
  const data = await res.json();
  if (data?.url) setRemoteUrl(key, data.url);
  return data;
}

/** Remove a key from the backend (and local remote map). Best-effort. */
export async function deleteFromBackend(key) {
  forgetRemoteUrl(key);
  if (!API_URL || !key) return;
  try {
    await fetch(
      `${API_URL}/api/settings/backgrounds/media/${encodeURIComponent(key)}`,
      { method: 'DELETE', headers: authHeaders() }
    );
  } catch (e) {
    console.warn('[backgrounds] remote delete failed', key, e);
  }
}

function mimeToExt(mime) {
  if (!mime) return '.bin';
  const m = String(mime).toLowerCase();
  if (m.includes('jpeg') || m.includes('jpg')) return '.jpg';
  if (m.includes('png')) return '.png';
  if (m.includes('webp')) return '.webp';
  if (m.includes('gif')) return '.gif';
  if (m.includes('mp4')) return '.mp4';
  if (m.includes('webm')) return '.webm';
  if (m.includes('svg')) return '.svg';
  return '.bin';
}
